/**
 * Candid patient-notes update modal.
 *
 * Save stays disabled until {@link CandidNotesFormState.isValid} — the notes
 * field must contain non-blank text. A successful POST refetches order data
 * and closes the dialog.
 *
 * @module patientNotesModal
 */
import { Modal, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import {
  CandidNotesFormState,
  EMPTY_CANDID_NOTES_FORM_STATE,
} from "../../../data-model/candidNotesFormState";
import { ModalId } from "../../../enums/modalId";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { useCandidNotesUploadSave } from "./hooks/useCandidNotesUploadSave";
import PatientNotesBody from "./patientNotesBody";

/**
 * Shape expected on `state.modal.metaData` when this modal is opened.
 */
export interface PatientNotesModalMetaData {
  /** Dialog title; falls back to "Candid Notes Update". */
  title?: string;
  /** PA order identifier for the notes POST. */
  orderId?: string;
  /** Organisation identifier for the notes POST. */
  orgId?: string;
  /** Patient MRN from `generalOrderDetails.demographics`. */
  mrn?: string;
}

/** Props for {@link PatientNotesModal}. */
interface PatientNotesModalProps {
  /**
   * Invoked when the dialog is dismissed so the parent can clear Redux modal
   * state.
   */
  onClose: () => void;
}

/**
 * Renders the Candid notes update dialog.
 *
 * @param props.onClose - Clears Redux modal state and unmounts this dialog
 * @returns The modal element
 */
const PatientNotesModal = ({ onClose }: PatientNotesModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metaData } = useSelector((state: RootState) => state.modal);
  const notesMetaData = metaData as PatientNotesModalMetaData | undefined;
  const { generalOrderDetails, fetchAll } = useDataAccumulatorForSingleOrder();
  const [formState, setFormState] = useState<CandidNotesFormState>(
    EMPTY_CANDID_NOTES_FORM_STATE,
  );

  const uploadContext = useMemo(
    () => ({
      orderId: generalOrderDetails?.order?.order_id ?? "",
      orgId: getOrgIdForFetchExternalWorklist() ?? "",
      mrn:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
    }),
    [notesMetaData, generalOrderDetails],
  );

  /**
   * Refetches order data, closes the dialog, and toasts after a successful
   * notes POST.
   */
  const onUploadSuccess = useCallback(async () => {
    await fetchAll(
      generalOrderDetails?.order?.order_id ?? "",
      getOrgIdForFetchExternalWorklist() ?? "",
      { force: true },
    );
    dispatch(closeModal());
    toast.success(ToastMessages.CANDID_NOTES_UPLOAD_INITIATED);
  }, [fetchAll, generalOrderDetails, dispatch]);

  const { handleSave, isSaveDisabled, saveButtonText } =
    useCandidNotesUploadSave({
      formState,
      context: uploadContext,
      onSuccess: onUploadSuccess,
    });

  return (
    <Modal
      dialogId={ModalId.PATIENT_NOTES}
      onSave={handleSave}
      title={notesMetaData?.title ?? "Candid Notes Update"}
      saveButtonText={saveButtonText}
      cancelText={"Cancel"}
      onClose={onClose}
      onCancel={onClose}
      disableSave={isSaveDisabled}
      maxHeightPercentage={40}
    >
      <div className="flex flex-col gap-2">
        <PatientNotesBody value={formState} onChange={setFormState} />
      </div>
    </Modal>
  );
};

export default PatientNotesModal;
